import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, RadioGroup, Stack, TextField, Typography, useTheme } from "@mui/material";
import { MprcButton, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { ButtonBack, ButtonClose } from "components/buttons";
import { IconButtonClose } from "components/iconButtons";
import { TopicAutoCompleteDataModel } from "components/MprcAutoComplete/AutoCompleteDataModel";
import { AutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical";
import { MprcRadio } from "components/MprcRadio";
import { MprcRequestSnackbarErrorReport, MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { MprcSwitch } from "components/MprcSwitch";
import { ManagedQuestionEvaluationDto, ManagedQuestionResponse, ManagedQuestionSurveyDto, QuestionOpenAIDto, QuestionType } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GeneratorOptions } from "views/assessment/generatorQuestion/GeneratorQuestionAI";
import { GeneratorQuestionAIList } from "views/assessment/generatorQuestion/GeneratorQuestionAIList";
import { GeneratorQuestionAIOption } from "views/assessment/generatorQuestion/GeneratorQuestionAIOption";
import { GeneratorQuestionAITitle } from "views/assessment/generatorQuestion/GeneratorQuestionAITitle";
import { QuestionGroupAutoCompleteHierarchical } from "views/assessment/questionBank/components/QuestionGroupAutoCompleteHierarchical";
import { IPostValidation, IValidationFields, QuestionContext, getEmptyQuestionDto } from "views/assessment/questionBank/Home/EditQuestion/EditQuestion";
import { validateAlternatives } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionAlternative";
import { validadeAvailableQuestion } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionAvailableQuestion";
import { validateEssay } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionEssay";
import { validateFillIn } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionFillIn";
import { validadeMainBox } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionMainbox";
import { validateStar } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionStar";
import { EditQuestionPointOthers, validatePointOther } from "views/assessment/questionBank/Home/EditQuestion/Evaluation/EditQuestionPointOthers";
import { TopicAutoComplete } from "views/foundation/topic/components/TopicAutoComplete";

/////////////////////////////////////////////////////////////////////////////////

type WhereSaveOptions = "questionBank" | "challenge";

interface IPros {
    publicationId: string;
    onClose: (success: boolean) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const GeneratorQuestionAIPub: React.FC<IPros> = (props) => {
    const { publicationId, onClose } = props;

    const api = useCoreApi();
    const theme = useTheme();
    const { t } = useTranslation("assessment");
    const { enqueueSnackbar } = useSnackbar();

    const [step, setStep] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [posting, setPosting] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [questions, setQuestions] = useState<QuestionOpenAIDto[]>([]);
    const [viewData, setViewData] = useState<ManagedQuestionResponse>(getEmptyQuestionDto(QuestionType.SimpleChoice, false, null, null));
    const [viewFieldsErrors, setViewFieldsErrors] = useState<ApiProblemErrors>();
    const [chatResponse, setChatResponse] = useState<string>();
    const [whereSave, setWhereSave] = useState<WhereSaveOptions>("challenge");
    const [topic, setTopic] = useState<TopicAutoCompleteDataModel[]>([]);
    const [questionGroup, setQuestionGroup] = useState<AutoCompleteHierarchicalModel[]>([]);
    const success = useRef<boolean>(false);
    const [option, setOption] = useState<GeneratorOptions>({
        numberOfQuestions: 5,
        numberOfAlternatives: 5,
        subject: "",
        createFeedback: true,
        recommentPub: true
    });

    const steps = [];
    const StepFirstPage = 1;
    const StepSecondPage = 2;
    const StepThirdPage = 3;
    const StepFourthPage = 4;

    ///
    function handleUpdateRecord(name: keyof ManagedQuestionResponse, v: any) {
        setViewFieldsErrors(LibUtils.deleteObjectProp(viewFieldsErrors, name));
        setViewData({ ...viewData, [name]: v });
    }

    ///
    function handleUpdateEvaluation(name: keyof ManagedQuestionEvaluationDto, v: any) {
        setViewFieldsErrors(LibUtils.deleteObjectProp(viewFieldsErrors, name));
        setViewData({ ...viewData, evaluation: { ...viewData.evaluation!, [name]: v } });
    }

    ///
    function handleUpdateSurvey(name: keyof ManagedQuestionSurveyDto, v: any) {
        setViewFieldsErrors(LibUtils.deleteObjectProp(viewFieldsErrors, name));
        setViewData({ ...viewData, survey: { ...viewData.survey!, [name]: v } });
    }

    ///
    async function getOpenAIQuestions() {
        setLoading(true);
        try {
            var result = await api.v1.questionBank.getOpenAiQuestionsFromPub({
                publicationId: publicationId,
                numberOfQuestions: option.numberOfQuestions,
                maxAlternatives: option.numberOfAlternatives,
                createFeedback: option.createFeedback,
            });
            setQuestions(result.data.questions);
            setChatResponse(result.data.messageContent);
            setStep(StepSecondPage);
        }
        catch (e: any) {
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    }

    ///
    async function saveAIQuestions() {
        setPosting(true);
        try {
            var saveQuestions = questions.filter(x => x.selected).map(x => ({ ...x, alternatives: x.alternatives.filter(x => x.alternativeText.length > 0 && !x.isInfo) }));

            var result = await api.v1.questionBank.saveAiQuestion({
                questions: saveQuestions,
                evaluation: viewData.evaluation,
                survey: null,
                tags: viewData.tags.map(x => x.label),
                availableQuestioBank: viewData.availableQuestioBank,
                exclusiveAssessmentId: null,
                questionGroupId: viewData.questionGroupId,
                isBlocked: viewData.isBlocked,
                publicationRecommendedId: option.recommentPub ? publicationId : null,
            });

            if (result.data) {
                success.current = true;
                enqueueSnackbar(t("GeneratorQuestionAI.SuccessMessage"), { variant: "success" });
            }

            onClose(true);
        }
        catch (e: any) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    }

    ///
    const resolveErrors = (e: any | unknown) => {
        var r = MprcResolveRequestErrors(e);
        setViewFieldsErrors(r.fieldsWithError);
        setViewError(r.otherErrors);
        if (r.fieldsWithError != null) enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });
    };

    // Validação de erros ao executar post.
    const vErrors = viewFieldsErrors ?? {};
    const postValidation: IPostValidation = {
        mainBox: {
            questionText: vErrors["questionText"],
        },
        alternativeBox: {
            alternatives: vErrors["alternatives"],
        },
        starBox: {
            alternatives: vErrors["alternatives"],
        },
        essayBox: {
            maxLen: vErrors["maxLen"],
            alternatives: vErrors["alternatives"],
        },
        fillInBox: {
            alternatives: vErrors["alternatives"],
            fillInMinValue: vErrors["fillInMinValue"],
            fillInMaxValue: vErrors["fillInMaxValue"],
        },
        feedbackBox: {
            trainingRecommended: vErrors["trainingRecommended"],
            knowledgePublicationRecommended: vErrors["knowledgePublicationRecommended"],
        },
        pointOtherBox: {
            points: vErrors["points"],
            isBlocked: vErrors["isBlocked"],
            allowInKnowledgeTests: vErrors["allowInKnowledgeTests"],
        },
        available: {
            questionGroupId: vErrors["questionGroupId"],
        }
    };

    // Validação dos campos de cada aba.
    const validationFields: IValidationFields = {
        mainBox: validadeMainBox(viewData),
        alternativeBox: validateAlternatives(viewData, false),
        starBox: validateStar(viewData),
        essayBox: validateEssay(viewData),
        fillInBox: validateFillIn(viewData, false),
        pointOtherBox: validatePointOther(viewData),
        available: validadeAvailableQuestion(viewData),
    };

    const canSave = (whereSave == "questionBank" ? questionGroup.length > 0 : topic.length > 0) && validatePointOther(viewData).points;

    // Ao iniciar pela primeira vez...
    useEffect(() => {
        async function getDefaultValues() {
            // Definir valores default
            if (viewData.evaluation != null) {

                setLoading(true);

                try {
                    const result = await api.v1.questionBank.managedQuestionDefaultValues({ questionGroupId: null });

                    if (result.data) {
                        setViewData({
                            ...viewData,
                            evaluation: {
                                ...viewData.evaluation,
                                allowInKnowledgeTests: result.data.allowInKnowledgeTests,
                                points: result.data.points
                            }
                        });
                    }
                }
                catch (e) {
                    setViewError(e);
                }
                finally {
                    setLoading(false);
                }
            }
        }

        getDefaultValues();

    }, []);

    ///
    steps[StepFirstPage] = () => {
        return (
            <>
                <DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: "400px" }}>
                    <GeneratorQuestionAIOption
                        header={
                            <>
                                <Typography variant="h4" component="p" color={theme.palette.ai.main}>
                                    {t("GeneratorQuestionAI.Pub.Title")}
                                </Typography>
                                <Typography variant="caption" component="p" color={theme.palette.ai.main}>
                                    {t("GeneratorQuestionAI.Pub.Subtitle")}
                                </Typography>
                            </>
                        }
                        option={option}
                        onChange={(value) => setOption(value)}
                        forSurvey={false}
                        hasRecommend={true}
                    />
                </DialogContent>

                <DialogActions>
                    <MprcButton
                        variant="contained"
                        color="primary"
                        disabled={!(option.numberOfQuestions >= 1 && option.numberOfQuestions <= 20) || !(option.numberOfAlternatives >= 1 && option.numberOfAlternatives <= 5)}
                        text={t("GeneratorQuestionAI.CreateButtonText")}
                        title={t("GeneratorQuestionAI.CreateButtonTitle")}
                        onClick={() => getOpenAIQuestions()}
                    />
                </DialogActions>
            </>
        );
    };

    ///
    steps[StepSecondPage] = () => {
        return (
            <>
                <DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: "400px" }}>
                    {
                        questions.length == 0
                            ?
                            <>
                                <Stack direction="column" spacing={0} alignItems="center" width="100%">
                                    <Typography variant="h3" color={theme.palette.error.main}>{t("GeneratorQuestionAI.Pub.ErrorTitle_L1")}</Typography>
                                    <Typography variant="h3" color={theme.palette.error.main}>{t("GeneratorQuestionAI.Pub.ErrorTitle_L2")}</Typography>
                                </Stack>
                                <Box sx={{
                                    borderColor: "muted.main",
                                    borderWidth: "1px",
                                    borderStyle: "solid",
                                    padding: "8px",
                                    minHeight: "320px"
                                }}>
                                    <TextField
                                        name="subject"
                                        fullWidth
                                        autoComplete="off"
                                        margin="dense"
                                        multiline
                                        autoFocus
                                        value={chatResponse}
                                        slotProps={{ input: { disableUnderline: true } }}
                                        disabled
                                        maxRows={14}
                                    />
                                </Box>
                            </>
                            :
                            <>
                                <GeneratorQuestionAITitle
                                    header={
                                        <>
                                            <Typography variant="h3" component="p" color={theme.palette.ai.main}>{t("GeneratorQuestionAI.ThirdPage.Title")}</Typography>
                                            <Typography variant="caption" component="p" color={theme.palette.ai.main}>{t("GeneratorQuestionAI.ThirdPage.Subtitle_L1")}</Typography>
                                            <Typography variant="caption" component="p" color={theme.palette.ai.main}>{t("GeneratorQuestionAI.ThirdPage.Subtitle_L2")}</Typography>
                                        </>
                                    }
                                />

                                <GeneratorQuestionAIList
                                    questions={questions}
                                    onChange={(value) => setQuestions(value)}
                                />
                            </>
                    }
                </DialogContent>

                <DialogActions>
                    {
                        questions.length == 0
                            ?
                            <ButtonClose variant="contained" onClick={() => onClose(false)} />
                            :
                            <>
                                <ButtonBack
                                    startIcon={null}
                                    onClick={() => setStep(StepFirstPage)}
                                />
                                <MprcButton
                                    variant="contained"
                                    color="primary"
                                    text={t("GeneratorQuestionAI.FirstPage.ContinueButtonText")}
                                    title={t("GeneratorQuestionAI.FirstPage.ContinueButtonTitle")}
                                    disabled={questions.length == 0 || questions.filter(x => x.selected).length == 0}
                                    onClick={() => setStep(StepThirdPage)}
                                />
                            </>
                    }
                </DialogActions>
            </>
        );
    };

    ///
    steps[StepThirdPage] = () => {
        return (
            <>
                <DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: "400px" }}>
                    <Grid container direction="column" spacing={4}>
                        <Grid>
                            <GeneratorQuestionAITitle
                                header={<Typography variant="h3" component="p" color={theme.palette.ai.main}>{t("GeneratorQuestionAI.Pub.WhereSaveTitle")}</Typography>}
                            />
                        </Grid>
                        <Grid>
                            <RadioGroup
                                value={whereSave}
                                onChange={e => setWhereSave(e.target.value as WhereSaveOptions)}
                            >
                                <MprcRadio
                                    value={"challenge"}
                                    label={t("GeneratorQuestionAI.Pub.SaveChallengeLabel")}
                                />
                                <MprcRadio
                                    value={"questionBank"}
                                    label={t("GeneratorQuestionAI.Pub.SaveQuestionBankLabel")}
                                />
                            </RadioGroup>
                        </Grid>
                        <Grid>
                            {
                                whereSave == "challenge"
                                    ?
                                    <TopicAutoComplete
                                        selectedItems={topic}
                                        applyKnowledgeTest
                                        placeholder={t("GeneratorQuestionAI.Pub.TopicPlaceholder")}
                                        onChange={(items) => {
                                            setTopic(items);
                                            handleUpdateRecord("questionGroupId", items.length > 0 ? items[0].sysQuestionGroupId : null);
                                        }}
                                        onError={(e) => MprcRequestSnackbarErrorReport(e)}
                                    />
                                    :
                                    <QuestionGroupAutoCompleteHierarchical
                                        label={t("GeneratorQuestionAI.Pub.QuestionGroupLabel")}
                                        multiSelect={false}
                                        required
                                        forSurvey={false}
                                        selectedItems={questionGroup}
                                        onChange={(items) => {
                                            setQuestionGroup(items);
                                            handleUpdateRecord("questionGroupId", items.length > 0 ? items[0].key : null);
                                        }}
                                        onError={(e) => MprcRequestSnackbarErrorReport(e)}
                                    />
                            }
                        </Grid>
                        <Grid>
                            <MprcSwitch
                                label={t("GeneratorQuestionAI.Pub.BlockedLabel")}
                                helperText={t("GeneratorQuestionAI.Pub.BlockedHint")}
                                checked={viewData.isBlocked}
                                onChange={(e) => handleUpdateRecord("isBlocked", e.target.checked)}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" width={"100%"}>
                        <Grid>
                            {
                                whereSave == "questionBank" &&
                                <MprcButton
                                    variant="contained"
                                    color="warning"
                                    disabled={!canSave}
                                    text={t("GeneratorQuestionAI.Pub.SetPointsAndOptionsText")}
                                    title={t("GeneratorQuestionAI.Pub.SetPointsAndOptionsTitle")}
                                    onClick={() => setStep(StepFourthPage)}
                                />
                            }
                        </Grid>
                        <Grid size="grow">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <ButtonBack
                                    startIcon={null}
                                    onClick={() => setStep(StepSecondPage)}
                                />
                                <MprcButton
                                    variant="contained"
                                    color="primary"
                                    disabled={!canSave}
                                    text={t("GeneratorQuestionAI.FouthPage.SaveButtonText")}
                                    title={t("GeneratorQuestionAI.FouthPage.SaveButtonTitle")}
                                    onClick={() => saveAIQuestions()}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogActions>

            </>
        );
    };

    ///
    steps[StepFourthPage] = () => {
        return (
            <>
                <DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: "400px" }}>
                    <GeneratorQuestionAITitle
                        header={<Typography variant="h3" color={theme.palette.ai.main}>{t("GeneratorQuestionAI.Pub.SetPointsTitle")}</Typography>}
                    />
                    <EditQuestionPointOthers
                        onError={error => resolveErrors(error)}
                    />
                </DialogContent>

                <DialogActions>
                    <ButtonBack
                        startIcon={null}
                        onClick={() => setStep(StepThirdPage)}
                    />
                    <MprcButton
                        variant="contained"
                        color="primary"
                        disabled={!canSave}
                        text={t("GeneratorQuestionAI.FouthPage.SaveButtonText")}
                        title={t("GeneratorQuestionAI.FouthPage.SaveButtonTitle")}
                        onClick={() => saveAIQuestions()}
                    />
                </DialogActions>
            </>
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            onClose={() => onClose(success.current)}
        >
            <MprcRequestErrorReport error={viewError} />
            <MprcRequestIndicator type={loading ? "request" : "post"} visible={loading || posting} />

            <DialogTitle>
                <Grid container justifyContent="space-between">
                    <Grid>
                        <Typography variant="h4" component="h1" color="muted.main">
                            {t("GeneratorQuestionAI.TitleBeta")}
                        </Typography>
                    </Grid>
                    <Grid>
                        <IconButtonClose
                            color="inherit"
                            size="small"
                            onClick={() => onClose(success.current)}
                        />
                    </Grid>
                </Grid>
            </DialogTitle>

            <QuestionContext.Provider
                value={{
                    viewData,
                    validationFields,
                    postValidation,
                    handleUpdateRecord,
                    handleUpdateEvaluation,
                    handleUpdateSurvey,
                    forSurvey: false,
                    readonly: false,
                    editMode: "default",
                }}
            >

                {steps[step]()}

            </QuestionContext.Provider>
        </Dialog>
    );
};
